// Kalendář: všechny výpočty dnů a hodin v časové zóně organizace (CLAUDE.md pravidlo 11), ne v zóně prohlížeče.
// Den se označuje klíčem "YYYY-MM-DD" (kalendářní den v zóně organizace), instanty jsou ISO řetězce.
import type { EventItem } from '@wac-buddy/shared'

export const DEFAULT_TIMEZONE = 'Europe/Prague'
/** Svislá osa dne a týdne: od 6:00 do 20:00. */
export const DAY_START_HOUR = 6
export const DAY_END_HOUR = 20

export type CalendarMode = 'day' | 'week'

export interface ZonedParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

const formatters = new Map<string, Intl.DateTimeFormat>()

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    })
    formatters.set(timeZone, f)
  }
  return f
}

/** Rozloží okamžik na nástěnný čas v dané zóně. */
export function zonedParts(date: Date, timeZone: string): ZonedParts {
  const out: Record<string, number> = {}
  for (const p of partsFormatter(timeZone).formatToParts(date)) {
    if (p.type !== 'literal') out[p.type] = Number(p.value)
  }
  return {
    year: out.year!,
    month: out.month!,
    day: out.day!,
    hour: out.hour! % 24,
    minute: out.minute!,
    second: out.second!,
  }
}

/** Posun zóny vůči UTC v milisekundách v daném okamžiku (kladný východně od Greenwiche). */
export function zoneOffsetMs(date: Date, timeZone: string): number {
  const p = zonedParts(date, timeZone)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asUtc - Math.floor(date.getTime() / 1000) * 1000
}

const pad = (n: number) => String(n).padStart(2, '0')

/** Nástěnný čas v zóně na okamžik. Neexistující čas (jarní posun) skočí dopředu, dvojznačný (podzim) bere druhý výskyt. */
export function zonedToDate(
  p: { year: number; month: number; day: number; hour?: number; minute?: number },
  timeZone: string,
): Date {
  const guess = Date.UTC(p.year, p.month - 1, p.day, p.hour ?? 0, p.minute ?? 0)
  const first = guess - zoneOffsetMs(new Date(guess), timeZone)
  const second = guess - zoneOffsetMs(new Date(first), timeZone)
  return new Date(second)
}

// ---- Klíče dnů (čistá kalendářní aritmetika, bez zón) ----

export function parseDayKey(key: string): { year: number; month: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key)
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  const check = new Date(Date.UTC(year, month - 1, day))
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1) return null
  if (check.getUTCDate() !== day) return null
  return { year, month, day }
}

function keyOfUtc(ms: number): string {
  const d = new Date(ms)
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

function utcOfKey(key: string): number {
  const p = parseDayKey(key)
  if (!p) throw new Error(`Neplatný den: ${key}`)
  return Date.UTC(p.year, p.month - 1, p.day)
}

export function addDays(key: string, days: number): string {
  return keyOfUtc(utcOfKey(key) + days * 86_400_000)
}

/** Den v týdnu, pondělí = 0 až neděle = 6. */
export function weekdayIndex(key: string): number {
  return (new Date(utcOfKey(key)).getUTCDay() + 6) % 7
}

/** Pondělí týdne, do kterého den patří. */
export function startOfWeekKey(key: string): string {
  return addDays(key, -weekdayIndex(key))
}

export function weekDayKeys(key: string): string[] {
  const monday = startOfWeekKey(key)
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i))
}

/** Okamžik jako kalendářní den v zóně. */
export function dayKeyOf(date: Date, timeZone: string): string {
  const p = zonedParts(date, timeZone)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

export function todayKey(timeZone: string, now: Date = new Date()): string {
  return dayKeyOf(now, timeZone)
}

/** Půlnoc začátku dne v zóně jako okamžik. */
export function startOfDay(key: string, timeZone: string): Date {
  const p = parseDayKey(key)
  if (!p) throw new Error(`Neplatný den: ${key}`)
  return zonedToDate(p, timeZone)
}

export interface CalendarRange {
  /** Začátek prvního dne (včetně), ISO. */
  from: string
  /** Začátek dne po posledním (vyloučeně), ISO. */
  to: string
  /** Klíč do cache. */
  key: string
  days: string[]
}

export function rangeFor(mode: CalendarMode, dayKey: string, timeZone: string): CalendarRange {
  const days = mode === 'day' ? [dayKey] : weekDayKeys(dayKey)
  const from = startOfDay(days[0]!, timeZone).toISOString()
  const to = startOfDay(addDays(days[days.length - 1]!, 1), timeZone).toISOString()
  return { from, to, key: `${mode}:${from}:${to}`, days }
}

/** O kolik dnů se přesune šipka: den o 1, týden o 7. */
export function shiftKey(mode: CalendarMode, dayKey: string, direction: -1 | 1): string {
  return addDays(dayKey, direction * (mode === 'day' ? 1 : 7))
}

// ---- Formátování ----

/** "14:30" v zóně organizace. */
export function formatHM(iso: string, timeZone: string): string {
  const p = zonedParts(new Date(iso), timeZone)
  return `${p.hour}:${pad(p.minute)}`
}

const labelFormatters = new Map<string, Intl.DateTimeFormat>()
function labelFormatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const id = JSON.stringify(options)
  let f = labelFormatters.get(id)
  if (!f) {
    f = new Intl.DateTimeFormat('cs-CZ', { ...options, timeZone: 'UTC' })
    labelFormatters.set(id, f)
  }
  return f
}

/** "úterý 13. října" */
export function formatDayLong(key: string): string {
  return labelFormatter({ weekday: 'long', day: 'numeric', month: 'long' }).format(
    new Date(utcOfKey(key)),
  )
}

/** "út" */
export function formatWeekdayShort(key: string): string {
  return labelFormatter({ weekday: 'short' }).format(new Date(utcOfKey(key)))
}

/** "13. 10." */
export function formatDayShort(key: string): string {
  const p = parseDayKey(key)!
  return `${p.day}. ${p.month}.`
}

/** "12. 10. až 18. 10. 2026" pro hlavičku týdne. */
export function formatWeekLabel(key: string): string {
  const days = weekDayKeys(key)
  const first = parseDayKey(days[0]!)!
  const last = parseDayKey(days[6]!)!
  return `${first.day}. ${first.month}. až ${last.day}. ${last.month}. ${last.year}`
}

// ---- Vstupy datetime-local a date ----

/** ISO na hodnotu pro `<input type="datetime-local">` ("2026-10-13T14:30") v zóně organizace. */
export function isoToLocalInput(iso: string, timeZone: string): string {
  const p = zonedParts(new Date(iso), timeZone)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`
}

/** Hodnota `datetime-local` v zóně organizace na ISO (UTC). Neplatný vstup vrátí null. */
export function localInputToIso(value: string, timeZone: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::\d{2})?$/.exec(value)
  if (!m) return null
  const day = parseDayKey(m[1]!)
  const hour = Number(m[2])
  const minute = Number(m[3])
  if (!day || hour > 23 || minute > 59) return null
  return zonedToDate({ ...day, hour, minute }, timeZone).toISOString()
}

const LOCAL_RE = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/

/** Přičte minuty k hodnotě `datetime-local` (nástěnná aritmetika bez zóny). */
export function addMinutesLocal(value: string, minutes: number): string {
  const m = LOCAL_RE.exec(value)
  if (!m || !parseDayKey(m[1]!)) return value
  const base = utcOfKey(m[1]!) + (Number(m[2]) * 60 + Number(m[3]) + minutes) * 60_000
  const d = new Date(base)
  return `${keyOfUtc(base)}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

/** Rozdíl dvou hodnot `datetime-local` v minutách (nástěnně), null při neplatném vstupu. */
export function diffMinutesLocal(from: string, to: string): number | null {
  const a = LOCAL_RE.exec(from)
  const b = LOCAL_RE.exec(to)
  if (!a || !b || !parseDayKey(a[1]!) || !parseDayKey(b[1]!)) return null
  const ma = utcOfKey(a[1]!) / 60_000 + Number(a[2]) * 60 + Number(a[3])
  const mb = utcOfKey(b[1]!) / 60_000 + Number(b[2]) * 60 + Number(b[3])
  return mb - ma
}

/** Celodenní událost: začátek je půlnoc prvního dne. */
export function allDayStartIso(dayKey: string, timeZone: string): string | null {
  return parseDayKey(dayKey) ? startOfDay(dayKey, timeZone).toISOString() : null
}

/** Celodenní událost: konec je půlnoc dne po posledním dni (vyloučeně), takže jeden den má konec > začátek. */
export function allDayEndIso(lastDayKey: string, timeZone: string): string | null {
  return parseDayKey(lastDayKey) ? startOfDay(addDays(lastDayKey, 1), timeZone).toISOString() : null
}

/** Poslední den celodenní události z jejího `endAt` (vyloučeného). */
export function allDayLastDayKey(endIso: string, timeZone: string): string {
  return dayKeyOf(new Date(new Date(endIso).getTime() - 1), timeZone)
}

// ---- Události v dnech ----

export function overlapsDay(event: EventItem, key: string, timeZone: string): boolean {
  const dayStart = startOfDay(key, timeZone).getTime()
  const dayEnd = startOfDay(addDays(key, 1), timeZone).getTime()
  const start = new Date(event.startAt).getTime()
  const end = new Date(event.endAt).getTime()
  return start < dayEnd && Math.max(end, start + 1) > dayStart
}

export function allDayEventsOn(events: EventItem[], key: string, timeZone: string): EventItem[] {
  return events
    .filter((e) => e.allDay && overlapsDay(e, key, timeZone))
    .sort((a, b) => a.title.localeCompare(b.title, 'cs'))
}

/** Události dne (celodenní první, pak podle času) pro seznamové zobrazení. */
export function eventsOnDay(events: EventItem[], key: string, timeZone: string): EventItem[] {
  return events
    .filter((e) => overlapsDay(e, key, timeZone))
    .sort((a, b) => {
      if (a.allDay !== b.allDay) return a.allDay ? -1 : 1
      return a.startAt.localeCompare(b.startAt) || a.title.localeCompare(b.title, 'cs')
    })
}

export interface PlacedEvent {
  event: EventItem
  /** Minuty od začátku osy (od 6:00). */
  top: number
  /** Výška v minutách, nejméně `minMinutes`. */
  height: number
  lane: number
  lanes: number
}

/** Rozloží události dne (bez celodenních) na osu 6:00 až 20:00 a vedle sebe, když se překrývají. */
export function layoutDayEvents(
  events: EventItem[],
  key: string,
  timeZone: string,
  minMinutes = 48,
): PlacedEvent[] {
  const axisStart = DAY_START_HOUR * 60
  const axisEnd = DAY_END_HOUR * 60
  const dayStart = startOfDay(key, timeZone).getTime()
  const dayEnd = startOfDay(addDays(key, 1), timeZone).getTime()
  const wall = (ms: number) => {
    const p = zonedParts(new Date(ms), timeZone)
    return p.hour * 60 + p.minute
  }

  const items = events
    .filter((e) => !e.allDay && overlapsDay(e, key, timeZone))
    .map((event) => {
      const start = new Date(event.startAt).getTime()
      const end = new Date(event.endAt).getTime()
      const startMin = start <= dayStart ? 0 : wall(start)
      const endMin = end >= dayEnd ? 24 * 60 : wall(end)
      let from = Math.max(startMin, axisStart)
      let to = Math.min(endMin, axisEnd)
      if (to <= from) {
        // Mimo osu (před 6:00 nebo po 20:00): drobný blok u okraje, ať událost nezmizí.
        if (endMin <= axisStart) {
          from = axisStart
          to = axisStart + minMinutes
        } else {
          to = axisEnd
          from = axisEnd - minMinutes
        }
      }
      to = Math.max(to, from + minMinutes)
      if (to > axisEnd) {
        to = axisEnd
        from = Math.max(axisStart, to - minMinutes)
      }
      return { event, from, to }
    })
    .sort((a, b) => a.from - b.from || a.to - b.to)

  // Přidělení pruhů v shlucích překrývajících se událostí.
  const placed: PlacedEvent[] = []
  let cluster: { event: EventItem; from: number; to: number; lane: number }[] = []
  let clusterEnd = -1
  const flush = () => {
    const lanes = cluster.reduce((max, c) => Math.max(max, c.lane + 1), 1)
    for (const c of cluster) {
      placed.push({
        event: c.event,
        top: c.from - axisStart,
        height: c.to - c.from,
        lane: c.lane,
        lanes,
      })
    }
    cluster = []
    clusterEnd = -1
  }
  for (const item of items) {
    if (cluster.length && item.from >= clusterEnd) flush()
    const used = new Set(cluster.filter((c) => c.to > item.from).map((c) => c.lane))
    let lane = 0
    while (used.has(lane)) lane++
    cluster.push({ ...item, lane })
    clusterEnd = Math.max(clusterEnd, item.to)
  }
  if (cluster.length) flush()
  return placed
}
